/**
 * Quality Tab Component - NCRs list
 */

import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { NonConformanceReport, CustomerProfile } from '../../lib/api';
import { getSeverityColor } from './utils';
import { useFormatters } from '../../contexts/PreferencesContext';

interface QualityTabProps {
  customer: CustomerProfile;
  ncrs: NonConformanceReport[];
  ncrsLoading: boolean;
}

export function QualityTab({ customer, ncrs, ncrsLoading }: QualityTabProps) {
  const { formatDate } = useFormatters();
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          Non-Conformance Reports (NCRs)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {ncrsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : ncrs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 opacity-50 text-green-500" />
            <p>No NCRs found</p>
            <p className="text-sm mt-2">This customer has a clean quality record</p>
          </div>
        ) : (
          <div className="space-y-4">
            {ncrs.map((ncr) => (
              <div
                key={ncr.id}
                className="border rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/quality/ncr/${ncr.id}/edit`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold">{ncr.ncr_number}</p>
                    <p className="text-sm text-muted-foreground">
                      {ncr.part_number} - {ncr.description}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={getSeverityColor(ncr.severity)}>
                      {ncr.severity_display}
                    </Badge>
                    <Badge variant="outline">{ncr.status_display}</Badge>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground mb-2">{ncr.defect_description}</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">NCR Date: </span>
                    <span>{formatDate(ncr.ncr_date)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Raised By: </span>
                    <span>{ncr.raised_by_name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Quantity Affected: </span>
                    <span className="font-semibold text-red-600">{ncr.quantity_affected}</span>
                  </div>
                  {ncr.po_number && (
                    <div>
                      <span className="text-muted-foreground">PO: </span>
                      <span>{ncr.po_number}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {ncrs.length === 10 && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/quality?customer=${customer.company}`)}
              >
                View All NCRs
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
